import i18next from "i18next";

import { DEFAULT_LANGUAGE, intlTagFor, normalizeLanguage } from "./languages";

/**
 * The one place the app turns a value into a localized string.
 *
 * Everything here is pure and takes the locale explicitly as its first argument
 * after the value, so it can be unit-tested without React, without i18next and
 * without a provider. The React binding lives in `useFormatters.ts`; utilities
 * that cannot use hooks read the active language with `activeLanguage()`.
 *
 * Why this exists: the codebase had four currency formatters and 59 hardcoded
 * `"en-US"` tags, so a German user saw German UI copy around American numbers
 * and dates. Formatting is data, not copy — it has to follow the reader's
 * locale, not the translation catalog.
 *
 * Scope rule: DISPLAY ONLY. Never route an ISO date or a value that ends up in
 * a URL, a filter or an API payload through these functions — a German locale
 * turns "1234.5" into "1.234,5" and the backend will not thank you.
 */

/** What `formatDate` accepts. A string is parsed by `Date`, a number is epoch ms. */
export type DateInput = Date | string | number | null | undefined;

/** Length of a formatted date. Maps onto Intl's `dateStyle`. */
export type DateStyle = "short" | "medium" | "long";

/**
 * Resolve anything the app might hold — a language code ("de"), a full tag
 * ("pt-BR"), or nothing — onto the BCP-47 tag Intl should format with.
 *
 * Shipped languages win: "pt" and "pt-BR" both format as pt-BR, "zh" as zh-CN,
 * because `SUPPORTED_LANGUAGES` is the app's whole locale universe and its
 * `intlTag` is the deliberate regional choice. A tag we don't ship is passed
 * through when Intl recognizes it, so an explicit `formatDate(d, "sv-SE")` is
 * honored rather than silently Americanized.
 */
export function toIntlTag(locale: string | null | undefined): string {
  const code = normalizeLanguage(locale);
  if (code) return intlTagFor(code);
  if (!locale) return intlTagFor(DEFAULT_LANGUAGE);
  try {
    const [canonical] = Intl.getCanonicalLocales(locale);
    return canonical ?? intlTagFor(DEFAULT_LANGUAGE);
  } catch {
    return intlTagFor(DEFAULT_LANGUAGE);
  }
}

/**
 * The language the app is currently showing, for non-React callers.
 *
 * Reads the live i18next singleton (the same instance `lib/i18n/index.ts`
 * initializes) at CALL time, never at import time — a module-level snapshot
 * would freeze the locale at whatever it was when the bundle loaded and ignore
 * every later language switch.
 */
export function activeLanguage(): string {
  return normalizeLanguage(i18next.language) ?? DEFAULT_LANGUAGE;
}

/**
 * Intl constructors are expensive (they build a locale data pipeline) and the
 * grid calls formatters once per visible cell, so every instance is cached by
 * locale + options. Options are serialized with sorted keys so `{a,b}` and
 * `{b,a}` share one entry.
 */
const formatterCache = new Map<string, unknown>();

function cacheKey(kind: string, tag: string, options: object | undefined): string {
  if (!options) return `${kind}|${tag}|`;
  const entries = Object.entries(options)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `${kind}|${tag}|${JSON.stringify(entries)}`;
}

function cached<T>(kind: string, tag: string, options: object | undefined, build: () => T): T {
  const key = cacheKey(kind, tag, options);
  const hit = formatterCache.get(key);
  if (hit) return hit as T;
  const built = build();
  formatterCache.set(key, built);
  return built;
}

/** The memoized `Intl.DateTimeFormat` for a locale + options. Exported for tests. */
export function dateTimeFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const tag = toIntlTag(locale);
  return cached("date", tag, options, () => new Intl.DateTimeFormat(tag, options));
}

/** The memoized `Intl.NumberFormat` for a locale + options. Exported for tests. */
export function numberFormatter(
  locale: string,
  options?: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const tag = toIntlTag(locale);
  return cached("number", tag, options, () => new Intl.NumberFormat(tag, options));
}

/** The memoized `Intl.RelativeTimeFormat` for a locale. Exported for tests. */
export function relativeTimeFormatter(
  locale: string,
  options: Intl.RelativeTimeFormatOptions = { numeric: "auto" },
): Intl.RelativeTimeFormat {
  const tag = toIntlTag(locale);
  return cached("relative", tag, options, () => new Intl.RelativeTimeFormat(tag, options));
}

/** Test seam: drop every memoized Intl instance. */
export function clearFormatterCache(): void {
  formatterCache.clear();
}

/**
 * Coerce to a usable Date, or null.
 *
 * Returning null (and, above, "") instead of throwing is deliberate: these run
 * inside render, and a single malformed timestamp from the index must cost one
 * empty cell, not the whole page.
 */
export function toDate(value: DateInput): Date | null {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toFinite(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: "short" },
  medium: { dateStyle: "medium" },
  long: { dateStyle: "long" },
};

/**
 * How a date should be shaped: one of the three named lengths, or the raw Intl
 * options for the shapes `dateStyle` cannot express.
 *
 * The escape hatch exists because real call sites need parts, not lengths — a
 * day/month with no year for a billing period ("Sep 1 – Sep 30"), a weekday
 * ("Tue, Sep 17"), a bare month name. Rebuilding those from `dateStyle` would
 * change the English output, and English is the no-op path.
 */
export type DateFormat = DateStyle | Intl.DateTimeFormatOptions;

function dateOptions(style: DateFormat): Intl.DateTimeFormatOptions {
  if (typeof style === "string") return DATE_STYLES[style] ?? DATE_STYLES.medium;
  return style;
}

/** `Sep 17, 2026` in en-US, `17. Sept. 2026` in de-DE. "" for unusable input. */
export function formatDate(
  date: DateInput,
  locale: string,
  style: DateFormat = "medium",
): string {
  const parsed = toDate(date);
  if (!parsed) return "";
  return dateTimeFormatter(locale, dateOptions(style)).format(parsed);
}

/** Date plus a short clock time, both in the reader's locale. "" for unusable input. */
export function formatDateTime(
  date: DateInput,
  locale: string,
  style: DateFormat = "medium",
): string {
  const parsed = toDate(date);
  if (!parsed) return "";
  const base = dateOptions(style);
  // Explicit options that already name a time part own the whole shape; adding
  // `timeStyle` alongside `hour`/`minute` is a TypeError in Intl.
  const hasTime =
    "timeStyle" in base ||
    "hour" in base ||
    "minute" in base ||
    "second" in base ||
    "dayPeriod" in base;
  return dateTimeFormatter(locale, hasTime ? base : { ...base, timeStyle: "short" }).format(parsed);
}

/** Clock time only — 12-hour in en-US, 24-hour in de-DE. "" for unusable input. */
export function formatTime(date: DateInput, locale: string): string {
  const parsed = toDate(date);
  if (!parsed) return "";
  return dateTimeFormatter(locale, { timeStyle: "short" }).format(parsed);
}

/** Thresholds for picking a relative unit, largest unit that still reads naturally. */
const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ["second", 1000],
  ["minute", 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["week", 7 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["year", 365 * 24 * 60 * 60 * 1000],
];

/**
 * `in 3 days` / `2 months ago`, localized.
 *
 * `now` is a parameter so callers can stamp a whole list from one instant and
 * so this is testable without faking the clock. Past is negative, future
 * positive, and `numeric: "auto"` lets ICU say "yesterday" rather than
 * "1 day ago" where the language has a word for it.
 */
export function formatRelative(date: DateInput, locale: string, now: DateInput = new Date()): string {
  const parsed = toDate(date);
  const base = toDate(now) ?? new Date();
  if (!parsed) return "";

  const deltaMs = parsed.getTime() - base.getTime();
  const abs = Math.abs(deltaMs);

  let unit: Intl.RelativeTimeFormatUnit = "second";
  let size = 1000;
  for (const [candidate, ms] of RELATIVE_UNITS) {
    // Step up while the next unit still has at least one whole unit of distance.
    if (abs >= ms) {
      unit = candidate;
      size = ms;
    }
  }
  const value = Math.round(deltaMs / size);
  return relativeTimeFormatter(locale).format(value, unit);
}

/** `1,234.56` in en-US, `1.234,56` in de-DE. "" for NaN/Infinity/null. */
export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  const num = toFinite(value);
  if (num == null) return "";
  return numberFormatter(locale, options).format(num);
}

/** `12K`, `3.4M` — localized compact notation. "" for NaN/Infinity/null. */
export function formatCompact(
  value: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return formatNumber(value, locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
    ...options,
  });
}

/**
 * A RATIO to a percentage: `formatPercent(0.42, "en")` → "42%".
 *
 * Takes the ratio, not the already-multiplied number, because Intl's percent
 * style multiplies by 100 itself and doing it twice is the classic bug here.
 */
export function formatPercent(
  ratio: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return formatNumber(ratio, locale, {
    style: "percent",
    maximumFractionDigits: 0,
    ...options,
  });
}

/**
 * Money, with the symbol placed where the reader's language puts it:
 * `$1,234.56` in en-US, `1.234,56 €` in de-DE, `￥1,235` in ja-JP.
 *
 * The CURRENCY CODE is a workspace setting (what the money is), the LOCALE is
 * a user setting (how a number is written) — two different axes, which is why
 * they are two arguments. An unusable code degrades to a plain number rather
 * than swallowing the amount; an unusable amount returns "".
 */
export function formatCurrency(
  amount: number | null | undefined,
  locale: string,
  currencyCode: string,
  options?: Intl.NumberFormatOptions,
): string {
  const num = toFinite(amount);
  if (num == null) return "";
  try {
    return numberFormatter(locale, {
      style: "currency",
      currency: currencyCode,
      ...options,
    }).format(num);
  } catch {
    // RangeError: not a well-formed ISO 4217 code. Showing the number without a
    // symbol beats showing nothing where the number is the point.
    return formatNumber(num, locale, options);
  }
}

/**
 * Any kind of space ICU might place between a currency token and the digits.
 * `\s` already covers the non-breaking and narrow non-breaking spaces ICU
 * actually emits, so none of them are written out literally here.
 */
const SPACE_ONLY = /^\s+$/;

/**
 * Money formatted by Intl, but rendered with the workspace's OWN symbol.
 *
 * Coffee ships a 54-row currency table whose symbols are a product choice — the
 * settings picker shows them, and the grid has rendered them for years. ICU
 * disagrees for about twenty of those codes: it writes "SEK 1,234" where the
 * app writes "kr1,234", because ICU refuses ambiguous symbols in English.
 *
 * So we let ICU decide the two things it is authoritative about — grouping and
 * WHERE the symbol goes ("$1,235" vs "1.235 $") — and substitute the app's
 * symbol for ICU's token. The one adjustment: when ICU fell back to the ISO
 * code in PREFIX position it writes "SEK 1,234" with a separating space that a
 * symbol does not want, so that space is dropped ("kr1,234"). In suffix
 * position the space is idiomatic and stays ("1.235 kr").
 *
 * The net effect is that English output is byte-for-byte what it was before
 * this layer existed, while every other language finally gets its own layout —
 * which is rule 4 of the localization program: English is the no-op path.
 */
export function formatCurrencyWithSymbol(
  amount: number | null | undefined,
  locale: string,
  currencyCode: string,
  symbol: string | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  const num = toFinite(amount);
  if (num == null) return "";

  let parts: Intl.NumberFormatPart[];
  try {
    parts = numberFormatter(locale, {
      style: "currency",
      currency: currencyCode,
      ...options,
    }).formatToParts(num);
  } catch {
    return formatNumber(num, locale, options);
  }

  const tokenIndex = parts.findIndex(p => p.type === "currency");
  if (tokenIndex < 0 || !symbol) return parts.map(p => p.value).join("");

  const token = parts[tokenIndex].value;
  const usedIsoCode = token.toUpperCase() === currencyCode.toUpperCase();
  const isPrefix = parts.slice(tokenIndex + 1).some(p => p.type === "integer");

  const out: string[] = [];
  parts.forEach((part, i) => {
    if (i === tokenIndex) {
      out.push(symbol);
      return;
    }
    const isAdjacentSpace =
      (i === tokenIndex - 1 || i === tokenIndex + 1) &&
      part.type === "literal" &&
      SPACE_ONLY.test(part.value);
    if (usedIsoCode && isPrefix && isAdjacentSpace) return;
    out.push(part.value);
  });
  return out.join("");
}
